/**
 * eReg Signature Queue Page
 *
 * Displays pending signature requests for the current user.
 * Supports single and batch signing with credential verification.
 */

import { useState, useEffect } from 'react';
import {
  PenTool,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Check,
  X,
} from 'lucide-react';
import * as eregApi from '../lib/api';
import { PageHeader, RefreshButton, fieldCls } from '../../ui';
import type { SignatureQueueItem } from '../types';

export function SignatureQueue() {
  const [queueItems, setQueueItems] = useState<SignatureQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [showSignModal, setShowSignModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<SignatureQueueItem | null>(null);
  const [signError, setSignError] = useState<string | null>(null);

  useEffect(() => {
    loadQueue();
  }, []);

  const loadQueue = async () => {
    setLoading(true);
    try {
      const items = await eregApi.getSignatureQueue();
      setQueueItems(items);
    } catch (error) {
      console.error('Failed to load signature queue:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedItems.size === queueItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(queueItems.map(item => item.id)));
    }
  };

  const handleSelectItem = (id: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const handleSignSingle = (item: SignatureQueueItem) => {
    setSelectedAssignment(item);
    setShowSignModal(true);
    setSignError(null);
  };

  const handleDecline = (item: SignatureQueueItem) => {
    setSelectedAssignment(item);
    setShowDeclineModal(true);
  };

  const handleBatchSign = () => {
    if (selectedItems.size === 0) return;
    setSelectedAssignment(null);
    setShowSignModal(true);
    setSignError(null);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getDueDateStatus = (dueDate?: string) => {
    if (!dueDate) return null;
    const due = new Date(dueDate);
    const now = new Date();
    const daysUntilDue = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilDue < 0) {
      return { label: 'Overdue', color: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' };
    } else if (daysUntilDue === 0) {
      return { label: 'Due Today', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300' };
    } else if (daysUntilDue <= 3) {
      return { label: `${daysUntilDue}d left`, color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300' };
    }
    return null;
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <PageHeader
        title="Signature Queue"
        subtitle="Documents awaiting your signature"
        actions={
          <>
            {selectedItems.size > 0 && (
              <button
                onClick={handleBatchSign}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150"
              >
                <PenTool size={18} />
                <span>Sign Selected ({selectedItems.size})</span>
              </button>
            )}
            <RefreshButton onClick={loadQueue} loading={loading} />
          </>
        }
      />

      {/* Queue Count */}
      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
        <Clock size={16} />
        <span>{queueItems.length} document{queueItems.length !== 1 ? 's' : ''} pending your signature</span>
      </div>

      {/* Queue Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 dark:border-primary-400"></div>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading signature queue...</p>
          </div>
        ) : queueItems.length === 0 ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">
            <CheckCircle size={48} className="mx-auto mb-4 text-green-500 dark:text-green-400" />
            <p className="text-lg font-medium text-gray-900 dark:text-gray-100">All caught up!</p>
            <p className="text-sm mt-1">No documents awaiting your signature</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="w-12 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedItems.size === queueItems.length && queueItems.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Document
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Study
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Requested By
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Signature Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Due Date
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
              {queueItems.map((item) => {
                const doc = item.signature_request?.document;
                const request = item.signature_request;
                const dueStatus = getDueDateStatus(request?.due_date);

                return (
                  <tr key={item.id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item.id)}
                        onChange={() => handleSelectItem(item.id)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-100 rounded-lg dark:bg-gray-700">
                          <FileText size={20} className="text-gray-600 dark:text-gray-300" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {doc?.document_name || 'Unknown Document'}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {doc?.document_type} - v{doc?.version_number}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {doc?.protocol?.protocol_number || '-'}
                        </div>
                        <div className="text-gray-500 dark:text-gray-400 truncate max-w-xs">
                          {doc?.protocol?.study_nickname || doc?.protocol?.protocol_title || doc?.folder?.path}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {request?.requested_by_user?.full_name || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300">
                        {item.signature_meaning}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <div className="text-gray-900 dark:text-gray-100">
                          {formatDate(request?.due_date)}
                        </div>
                        {dueStatus && (
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${dueStatus.color}`}>
                            {dueStatus.label}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleSignSingle(item)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150"
                        >
                          <PenTool size={14} />
                          <span>Sign</span>
                        </button>
                        <button
                          onClick={async () => {
                            if (!doc?.id) return;
                            // Open synchronously inside the click gesture so popup
                            // blockers allow it, then set the presigned URL.
                            const win = window.open('about:blank', '_blank');
                            if (win) win.opener = null;
                            try {
                              const { url } = await eregApi.getDocumentViewUrl(doc.id);
                              if (win) {
                                win.location.href = url;
                              } else {
                                window.open(url, '_blank', 'noopener');
                              }
                            } catch (err) {
                              if (win) win.close();
                              console.error('Failed to open document preview:', err);
                            }
                          }}
                          className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors duration-150 dark:text-gray-400 dark:hover:bg-gray-700"
                          title="Preview Document"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleDecline(item)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors duration-150 dark:text-red-400 dark:hover:bg-red-900/20"
                          title="Decline"
                        >
                          <XCircle size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Sign Modal */}
      {showSignModal && (
        <SignModal
          assignment={selectedAssignment}
          selectedIds={selectedAssignment ? null : Array.from(selectedItems)}
          onClose={() => {
            setShowSignModal(false);
            setSelectedAssignment(null);
            setSignError(null);
          }}
          onSuccess={() => {
            setShowSignModal(false);
            setSelectedAssignment(null);
            setSelectedItems(new Set());
            loadQueue();
          }}
          error={signError}
          setError={setSignError}
        />
      )}

      {/* Decline Modal */}
      {showDeclineModal && selectedAssignment && (
        <DeclineModal
          assignment={selectedAssignment}
          onClose={() => {
            setShowDeclineModal(false);
            setSelectedAssignment(null);
          }}
          onSuccess={() => {
            setShowDeclineModal(false);
            setSelectedAssignment(null);
            loadQueue();
          }}
        />
      )}
    </div>
  );
}

// Sign Modal Component
function SignModal({
  assignment,
  selectedIds,
  onClose,
  onSuccess,
  error,
  setError,
}: {
  assignment: SignatureQueueItem | null;
  selectedIds: string[] | null;
  onClose: () => void;
  onSuccess: () => void;
  error: string | null;
  setError: (error: string | null) => void;
}) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const isBatch = selectedIds && selectedIds.length > 0;
  const docName = assignment?.signature_request?.document?.document_name;

  const handleSign = async () => {
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isBatch && selectedIds) {
        const result = await eregApi.batchSignDocuments({
          assignment_ids: selectedIds,
          password,
        });

        if (result.totalErrors > 0) {
          setError(`Signed ${result.totalSigned} documents. ${result.totalErrors} failed.`);
        } else {
          onSuccess();
        }
      } else if (assignment) {
        await eregApi.signDocument({
          assignment_id: assignment.id,
          password,
          signature_meaning: assignment.signature_meaning,
        });
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to sign document');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full dark:bg-gray-800">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {isBatch ? `Sign ${selectedIds?.length} Documents` : 'Sign Document'}
          </h2>
        </div>

        <div className="p-6 space-y-4">
          {!isBatch && docName && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg dark:bg-gray-900/40">
              <FileText size={24} className="text-gray-500 dark:text-gray-400" />
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">{docName}</div>
                <div className="text-sm text-primary-600 dark:text-primary-400">
                  {assignment?.signature_meaning}
                </div>
              </div>
            </div>
          )}

          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg dark:bg-amber-900/20 dark:border-amber-800">
            <div className="flex gap-3">
              <AlertCircle className="text-amber-600 dark:text-amber-400 flex-shrink-0" size={20} />
              <div className="text-sm text-amber-800 dark:text-amber-300">
                <p className="font-medium">Electronic Signature</p>
                <p className="mt-1">
                  By signing, you confirm your identity and that this signature is legally binding.
                  Enter your password to authenticate.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your account password"
              className={`w-full ${fieldCls}`}
              autoFocus
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-150 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSign}
              disabled={loading || !password}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Signing...</span>
                </>
              ) : (
                <>
                  <Check size={18} />
                  <span>Sign {isBatch ? `(${selectedIds?.length})` : ''}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Decline Modal Component
function DeclineModal({
  assignment,
  onClose,
  onSuccess,
}: {
  assignment: SignatureQueueItem;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDecline = async () => {
    setLoading(true);
    try {
      await eregApi.declineSignature(assignment.id, reason);
      onSuccess();
    } catch (error) {
      console.error('Failed to decline signature:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full dark:bg-gray-800">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Decline Signature Request</h2>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg dark:bg-gray-900/40">
            <FileText size={24} className="text-gray-500 dark:text-gray-400" />
            <div>
              <div className="font-medium text-gray-900 dark:text-gray-100">
                {assignment.signature_request?.document?.document_name}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {assignment.signature_meaning}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Reason for Declining (optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason..."
              rows={3}
              className={`w-full ${fieldCls}`}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-150 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleDecline}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Declining...</span>
                </>
              ) : (
                <>
                  <X size={18} />
                  <span>Decline</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
